import React, { useState } from 'react';
import style from '../content-type.module.scss';
import { Text as TText } from '../../types';
import moment from 'moment';
import { EditIcon, DeleteIcon } from '../../../../icons';
import { useMutate, useAppAndBranchId, usePermissions } from '../../../../hooks';
import { api } from '../../api';
import { Modal } from '../../../../components';
import { AddText } from './AddText';
import { errorMessage } from '../../../../utils/error';
import { useToast } from '../../../../../../../../helpers/commonFunction';

type Props = {
	text: TText & { id: string; updatedAt: string | null; createdAt: string };
	refetchContent: () => void;
};

export function Text(props: Props) {
	const { ErrorToast } = useToast()
	const [appId, branchId] = useAppAndBranchId();
	const { text, refetchContent } = props;
	const { mutate: deleteMutate, loading: deleteLoading } = useMutate(
		api.deleteContent,
	);

	const [modalVisibility, setModalVisibility] = useState({
		editContent: false,
		viewContent: false,
		deleteContent: false,
	});

	const [canEdit, canDelete] = usePermissions(['content.Edit', 'content.Delete']) as boolean[]

	function updateVisibility(
		key: keyof typeof modalVisibility,
		visibility: boolean,
	) {
		setModalVisibility((prev) => ({
			...prev,
			[key]: visibility,
		}));
	}

	return (
		<div className={style.contentTypeWrapper}>
			<div className={style.contentType} data-has-action={canEdit || canDelete} onClick={() => {
				updateVisibility('viewContent', true)
			}}>
				<div className={style.left}>
					<p>{text.contentValue.text}</p>
				</div>
				<div className={style.info}>
					<p>{text?.updatedAt ? moment(text?.updatedAt).format('DD MMM YYYY, hh:mm A') : ""}</p>
				</div>
				<div className={style.info}>
					<p>{moment(text.createdAt).format('DD MMM YYYY, hh:mm A')}</p>
					<div className={style.actions}>
						{(canEdit || canDelete) && <div className={style.line} />}
						{canEdit &&
							<button
								onClick={(e) => {
									e.stopPropagation()
									updateVisibility('editContent', true);
								}}
							>
								<EditIcon width="14" height="14" stroke="#141B34" />
							</button>
						}{canDelete &&
							<button
								onClick={async (e) => {
									e.stopPropagation()
									updateVisibility('deleteContent', true);
								}}
							>
								<DeleteIcon width="14" height="14" stroke="#D30000" />
							</button>
						}
					</div>
				</div>
			</div>
			{modalVisibility.viewContent && (
				<AddText
					mode="view"
					refetchContent={refetchContent}
					initialValue={{
						text: text.contentValue.text,
						alternates: text.contentValue.alternates,
						id: text.id,
					}}
					isOpen={modalVisibility.viewContent}
					close={() => {
						updateVisibility('viewContent', false);
					}}
				/>
			)}
			{modalVisibility.editContent && (
				<AddText
					mode="edit"
					refetchContent={refetchContent}
					initialValue={{
						text: text.contentValue.text,
						alternates: text.contentValue.alternates,
						id: text.id,
					}}
					isOpen={modalVisibility.editContent}
					close={() => updateVisibility('editContent', false)}
				/>
			)}
			<Modal
				styles={{
					'--ok-btn-bg': '#DE0000',
					'--ok-btn-filter': 'drop-shadow(0px 8px 13px rgba(211, 0, 73, 0.18))',
				}}
				okText="Delete"
				loading={deleteLoading}
				onOk={async () => {
					try {
						await deleteMutate(appId, branchId, text.id);
						refetchContent();
					} catch (err) {
						ErrorToast(errorMessage("delete", "text", err.code))
					}
				}}
				onCancel={() => updateVisibility('deleteContent', false)}
				isOpen={modalVisibility.deleteContent}
				title="Remove Chosen Text"
			>
				<div
					style={{
						padding: '18px 14px',
						color: '#393939',
						fontSize: 15,
						fontWeight: 500,
					}}
				>
					<p>Do you really want to remove the chosen text?</p>
				</div>
			</Modal>
		</div>
	);
}
